import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PercentageFitAndReliability from '../PercentageFitAndReliability';
import { UserContext } from '../../Context/UserContext/UserContextProvider';
import { GarmentObject } from '../../types/reducers/filterReducer';
import { colorDifference, getImageUrlFromGarmentObject, teeLabelsArray } from './garmentResultFunctions';
import { LoggingContext } from '../../Context/LoggingContext/LoggingContext';

function GarmentResult({ garmentObject }: { garmentObject: GarmentObject }) {
  const navigate = useNavigate();
  const location = useLocation();
  // USECONTEXTS
  const { userDetailsState } = useContext(UserContext);
  const { addLog } = useContext(LoggingContext);
  // USESTATE
  const [showMeasures, setshowMeasures] = useState(false);

  // What a result is clicked, we want to go to the garment page.
  const handleResultClick = React.useCallback((event) => {
    addLog(
      `result clicked (id=${event.target.id}). Navigating to: .../t-shirt/${garmentObject.newteetypes_id}`, location.pathname
      );
    window.scrollTo(0, 0);
    navigate(`/garmentpage/t-shirt/${garmentObject.newteetypes_id}/${garmentObject.size}`);
  }, []);

  const selectedColor = 'nocolor';

  return (
    <Card
      sx={{
        '& .MuiCardContent-root:last-child': {
          margin: 0,
          paddingTop: 0,
        },
      }}
      id={`garmentResult${garmentObject.teemeasuresaverages_id}`}
    >
      <CardHeader
        id={`garmentResultHeader${garmentObject.teemeasuresaverages_id}`}
        sx={{
          width: '100%',
          '.MuiCardHeader-content': {
            flex: '1 1 auto',
            width: '85%',
          },
          '.MuiCardHeader-subheader': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
          },
        }}
        action={
          <IconButton id='icon-button-arrow-icon' aria-label='settings' onClick={handleResultClick}>
            <ArrowForwardIcon id='arrow-icon' />
          </IconButton>
        }
        title={
          garmentObject.manufacturer ? garmentObject.manufacturer : 'Manufacturer data unavailable'
        }
        subheader={`${
          garmentObject.description ? garmentObject.description : 'description data unavailable'
        } (${garmentObject.size ? garmentObject.size : 'Size data unavailable'})`}
      />
      <CardMedia
        sx={{ height: 0, paddingTop: '100%', cursor: 'pointer', flex: '1 1 auto' }}
        image={getImageUrlFromGarmentObject(garmentObject, selectedColor)}
        id={`garmentResult${garmentObject.teemeasuresaverages_id}image`}
        onClick={handleResultClick}
      />

      <CardActions
        onClick={() => setshowMeasures(!showMeasures)}
        style={{ justifyContent: 'center' }}
      >
        <Button size='small' id={`viewMeasurementsButton${garmentObject.teemeasuresaverages_id}`}>
          View Measurements
        </Button>
        <IconButton aria-label='settings'>
          <ArrowDropDownIcon />
        </IconButton>
      </CardActions>
      {!showMeasures && (
        <PercentageFitAndReliability
          id={`percentageFitCircles${garmentObject.teemeasuresaverages_id}`}
          fitPercentage={garmentObject.fit * 100}
          reliabilityPercentage={garmentObject.reliability * 100}
        />
      )}
      {showMeasures && (
        <CardContent style={{ cursor: 'pointer' }}>
          <table style={{ margin: 'auto', marginBottom: '1rem', cursor: 'pointer' }}>
            <tbody>
              <tr>
                <th style={{ textAlign: 'left' }}>Location of measure</th>
                <th style={{ textAlign: 'end' }}>
                  Difference ({userDetailsState.unitcm === 1 ? 'cm' : 'Inches'})
                </th>
              </tr>
              {teeLabelsArray.map((thisLabel) => {
                let difference: string | number = 'N/A';
                const thisValue = garmentObject[thisLabel] || 0;
                if (
                  thisValue > 0 &&
                  (userDetailsState.idealGarmentMeasurements[thisLabel] || 0) > 0
                ) {
                  difference =
                    Math.round(
                      (thisValue / userDetailsState.unitcm -
                        (userDetailsState.idealGarmentMeasurements[thisLabel] || 0) /
                          userDetailsState.unitcm) *
                        2
                    ) / 2;
                  return (
                    <tr key={thisLabel}>
                      <td style={{ textDecoration: 'capitalize' }}>{thisLabel}</td>
                      <td style={{ color: colorDifference(difference), textAlign: 'end' }}>
                        {difference}
                      </td>
                    </tr>
                  );
                }
                return null;
              })}
            </tbody>
          </table>
          <Button
            style={{ height: '100%', width: '100%' }}
            variant='outlined'
            color='primary'
            size='small'
          >
            Click to see full details
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

export default GarmentResult;
